import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
interface BarChartProps {
  value: number;
  minimumThreshold: number;
  maximumThreshold: number;
  taxBenefit: number;
  labels?: {
    maximumThresholdLabel?: string;
    minimumThresholdLabel?: string;
    currentValueLabel?: string;
    taxBenefitLabel?: string;
  };
  locale?: string;
}
interface Dimensions {
  width: number;
  height: number;
}
interface Padding {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
interface LabelPosition {
  translateX: number;
  alignmentOffset: number;
}
export const HorizontalTaxDeductionBarChart: React.FC<BarChartProps> = t0 => {
  const $ = _c(107);
  const {
    value,
    minimumThreshold,
    maximumThreshold,
    taxBenefit,
    labels,
    locale
  } = t0;
  const containerRef = useRef(null);
  const labelRef = useRef(null);
  const sublabelRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: 0,
      height: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dimensions, setDimensions] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!containerRef.current) {
        return;
      }
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const {
            width,
            height
          } = entry.contentRect;
          setDimensions({
            width,
            height
          });
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const currentValue = Math.min(value, maximumThreshold);
  const [maxLabelWidth, setMaxLabelWidth] = useState(0);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!labelRef.current || !sublabelRef.current) {
        return;
      }
      const labelWidth = labelRef.current.getBBox().width;
      const sublabelWidth = sublabelRef.current.getBBox().width;
      setMaxLabelWidth(Math.max(labelWidth, sublabelWidth));
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = labels?.currentValueLabel;
  let t6;
  if ($[4] !== currentValue || $[5] !== dimensions.width || $[6] !== locale || $[7] !== t5) {
    t6 = [dimensions.width, currentValue, t5, locale];
    $[4] = currentValue;
    $[5] = dimensions.width;
    $[6] = locale;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  useLayoutEffect(t4, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      left: 0,
      right: 10,
      top: 50,
      bottom: 0
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const padding = t7;
  const chartWidth = dimensions.width - padding.left - padding.right;
  let t8;
  if ($[10] !== chartWidth || $[11] !== maximumThreshold) {
    t8 = val => val / maximumThreshold * chartWidth;
    $[10] = chartWidth;
    $[11] = maximumThreshold;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const scale = t8;
  let t9;
  if ($[13] !== currentValue || $[14] !== dimensions.width || $[15] !== maxLabelWidth || $[16] !== scale) {
    const getLabelPositions = () => {
      if (!dimensions.width) {
        return {
          translateX: 0,
          alignmentOffset: 0
        };
      }
      const basePosition = scale(currentValue);
      const maxPosition = dimensions.width - padding.right;
      const wouldOverflow = basePosition + maxLabelWidth + 6 > maxPosition;
      if (wouldOverflow) {
        const alignmentOffset = maxLabelWidth;
        return {
          translateX: maxPosition,
          alignmentOffset
        };
      }
      return {
        translateX: basePosition,
        alignmentOffset: 0
      };
    };
    t9 = getLabelPositions();
    $[13] = currentValue;
    $[14] = dimensions.width;
    $[15] = maxLabelWidth;
    $[16] = scale;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const labelPosition = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      width: "100%",
      position: "relative",
      fontSize: "0.9rem"
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = dimensions.width;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <defs><pattern id="mobileDiagonalHatch" patternUnits="userSpaceOnUse" width="4" height="4"><path d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2" style={{
          stroke: "#fafafa",
          strokeWidth: 1
        }} /></pattern></defs>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = dimensions.width - padding.right;
  let t14;
  if ($[20] !== t13) {
    t14 = <line x1={padding.left} y1={padding.top} x2={t13} y2={padding.top} stroke="black" strokeWidth="1" />;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = dimensions.width - padding.right;
  let t16;
  if ($[22] !== t15) {
    t16 = <line x1={padding.left} y1={padding.top + 40} x2={t15} y2={padding.top + 40} stroke="black" strokeWidth="1" />;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = value >= minimumThreshold ? 0 : 1;
  let t18;
  if ($[24] !== t17) {
    t18 = {
      transition: "all 0.5s",
      opacity: t17
    };
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = scale(minimumThreshold);
  const t20 = scale(minimumThreshold);
  let t21;
  if ($[26] !== t19 || $[27] !== t20) {
    t21 = <line x1={t19} y1={padding.top - 5} x2={t20} y2={padding.top} stroke="black" strokeWidth="1" />;
    $[26] = t19;
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  const t22 = scale(minimumThreshold);
  const t23 = labels?.minimumThresholdLabel || "Minstegrense";
  let t24;
  if ($[29] !== t22 || $[30] !== t23) {
    t24 = <text x={t22} y={padding.top - 30} textAnchor="start" alignmentBaseline="middle" transform="translate(-3, 0)">{t23}</text>;
    $[29] = t22;
    $[30] = t23;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  const t25 = scale(minimumThreshold);
  let t26;
  if ($[32] !== locale || $[33] !== minimumThreshold) {
    t26 = Intl.NumberFormat(locale || "no-NB").format(minimumThreshold);
    $[32] = locale;
    $[33] = minimumThreshold;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] !== t25 || $[36] !== t26) {
    t27 = <text x={t25} y={padding.top - 15} textAnchor="start" alignmentBaseline="middle" fontSize="0.75rem" transform="translate(-3, 0)">{t26} kr</text>;
    $[35] = t25;
    $[36] = t26;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  let t28;
  if ($[38] !== t18 || $[39] !== t21 || $[40] !== t24 || $[41] !== t27) {
    t28 = <g style={t18}>{t21}{t24}{t27}</g>;
    $[38] = t18;
    $[39] = t21;
    $[40] = t24;
    $[41] = t27;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  const t29 = value >= minimumThreshold ? 1 : 0;
  let t30;
  if ($[43] !== t29) {
    t30 = {
      transition: "all 0.5s",
      opacity: t29
    };
    $[43] = t29;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  const t31 = `translateX(${scale(currentValue) - 0.5}px)`;
  let t32;
  if ($[45] !== t31) {
    t32 = {
      transform: t31,
      transition: "all 0.5s"
    };
    $[45] = t31;
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  let t33;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      transition: "all 0.5s"
    };
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  const t34 = labelPosition.alignmentOffset > 0 ? 0 : 1;
  let t35;
  if ($[48] !== t34) {
    t35 = <line x1={0} y1={padding.top - 5} x2={0} y2={padding.top} stroke="black" strokeWidth="1" style={t33} opacity={t34} />;
    $[48] = t34;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] !== t32 || $[51] !== t35) {
    t36 = <g style={t32}>{t35}</g>;
    $[50] = t32;
    $[51] = t35;
    $[52] = t36;
  } else {
    t36 = $[52];
  }
  const t37 = `translateX(${labelPosition.translateX}px)`;
  let t38;
  if ($[53] !== t37) {
    t38 = {
      transform: t37,
      transition: "all 0.5s"
    };
    $[53] = t37;
    $[54] = t38;
  } else {
    t38 = $[54];
  }
  const t39 = `translateX(${-labelPosition.alignmentOffset}px)`;
  let t40;
  if ($[55] !== t39) {
    t40 = {
      transform: t39,
      transition: "all 0.5s"
    };
    $[55] = t39;
    $[56] = t40;
  } else {
    t40 = $[56];
  }
  const t41 = labels?.currentValueLabel || "Skattefradrag";
  let t42;
  if ($[57] !== t40 || $[58] !== t41) {
    t42 = <text ref={labelRef} x={0} y={padding.top - 30} textAnchor="start" alignmentBaseline="middle" style={t40}>{t41}</text>;
    $[57] = t40;
    $[58] = t41;
    $[59] = t42;
  } else {
    t42 = $[59];
  }
  const t43 = `translateX(${-labelPosition.alignmentOffset}px)`;
  let t44;
  if ($[60] !== t43) {
    t44 = {
      transform: t43,
      transition: "all 0.5s"
    };
    $[60] = t43;
    $[61] = t44;
  } else {
    t44 = $[61];
  }
  let t45;
  if ($[62] !== currentValue || $[63] !== locale) {
    t45 = Intl.NumberFormat(locale || "no-NB").format(currentValue);
    $[62] = currentValue;
    $[63] = locale;
    $[64] = t45;
  } else {
    t45 = $[64];
  }
  let t46;
  if ($[65] !== t44 || $[66] !== t45) {
    t46 = <text ref={sublabelRef} x={0} y={padding.top - 15} textAnchor="start" alignmentBaseline="middle" fontSize="0.75rem" style={t44}>{t45} kr</text>;
    $[65] = t44;
    $[66] = t45;
    $[67] = t46;
  } else {
    t46 = $[67];
  }
  let t47;
  if ($[68] !== t38 || $[69] !== t42 || $[70] !== t46) {
    t47 = <g style={t38}>{t42}{t46}</g>;
    $[68] = t38;
    $[69] = t42;
    $[70] = t46;
    $[71] = t47;
  } else {
    t47 = $[71];
  }
  let t48;
  if ($[72] !== t30 || $[73] !== t36 || $[74] !== t47) {
    t48 = <g style={t30}>{t36}{t47}</g>;
    $[72] = t30;
    $[73] = t36;
    $[74] = t47;
    $[75] = t48;
  } else {
    t48 = $[75];
  }
  const t49 = taxBenefit > 0 ? 1 : 0;
  const t50 = `translateX(${scale(taxBenefit)}px)`;
  let t51;
  if ($[76] !== t49 || $[77] !== t50) {
    t51 = {
      transition: "all 0.5s",
      opacity: t49,
      transform: t50
    };
    $[76] = t49;
    $[77] = t50;
    $[78] = t51;
  } else {
    t51 = $[78];
  }
  let t52;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <line x1={0} y1={padding.top + 40} x2={0} y2={padding.top + 40 + 5} stroke="black" strokeWidth="1" />;
    $[79] = t52;
  } else {
    t52 = $[79];
  }
  const t53 = labels?.taxBenefitLabel || "Du f\xE5r tilbake";
  let t54;
  if ($[80] !== t53) {
    t54 = <text x={0} y={padding.top + 40 + 20} textAnchor="start" alignmentBaseline="middle">{t53}</text>;
    $[80] = t53;
    $[81] = t54;
  } else {
    t54 = $[81];
  }
  let t55;
  if ($[82] !== locale || $[83] !== taxBenefit) {
    t55 = Intl.NumberFormat(locale || "no-NB").format(taxBenefit);
    $[82] = locale;
    $[83] = taxBenefit;
    $[84] = t55;
  } else {
    t55 = $[84];
  }
  let t56;
  if ($[85] !== t55) {
    t56 = <text x={0} y={padding.top + 40 + 36} textAnchor="start" alignmentBaseline="middle" fontSize="0.75rem">{t55} kr</text>;
    $[85] = t55;
    $[86] = t56;
  } else {
    t56 = $[86];
  }
  let t57;
  if ($[87] !== t51 || $[88] !== t54 || $[89] !== t56) {
    t57 = <g style={t51}>{t52}{t54}{t56}</g>;
    $[87] = t51;
    $[88] = t54;
    $[89] = t56;
    $[90] = t57;
  } else {
    t57 = $[90];
  }
  const t58 = padding.left;
  const t59 = padding.top;
  const t60 = scale(Math.min(value, maximumThreshold));
  const t61 = value < minimumThreshold ? 0.3 : 1;
  let t62;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = {
      transition: "all 0.5s"
    };
    $[91] = t62;
  } else {
    t62 = $[91];
  }
  let t63;
  if ($[92] !== t60 || $[93] !== t61) {
    t63 = <rect x={t58} y={t59} width={t60} height={40} fill="#000" opacity={t61} style={t62} />;
    $[92] = t60;
    $[93] = t61;
    $[94] = t63;
  } else {
    t63 = $[94];
  }
  let t64;
  if ($[95] !== scale || $[96] !== taxBenefit) {
    t64 = taxBenefit > 0 && <rect x={padding.left} y={padding.top} width={scale(taxBenefit)} height={40} fill="url(#mobileDiagonalHatch)" style={{
      transition: "all 0.5s"
    }} />;
    $[95] = scale;
    $[96] = taxBenefit;
    $[97] = t64;
  } else {
    t64 = $[97];
  }
  let t65;
  if ($[98] !== dimensions.width || $[99] !== t14 || $[100] !== t16 || $[101] !== t28 || $[102] !== t48 || $[103] !== t57 || $[104] !== t63 || $[105] !== t64) {
    t65 = <div ref={containerRef} style={t10}><svg width={t11} height="auto">{t12}{t14}{t16}{t28}{t48}{t57}{t63}{t64}</svg></div>;
    $[98] = dimensions.width;
    $[99] = t14;
    $[100] = t16;
    $[101] = t28;
    $[102] = t48;
    $[103] = t57;
    $[104] = t63;
    $[105] = t64;
    $[106] = t65;
  } else {
    t65 = $[106];
  }
  return t65;
};